import { useEffect, useRef, useState } from 'react'
import {
  AnalyticsEventName,
  RANKER_VERSION,
  type ChatResultPathway,
} from '@dupe/common/constants'
import type { ResultLeg } from '@dupe/common/types'
import useAnalytics from '~/src/hooks/useAnalytics'
import type { ResultSortContextValue } from '~/src/context/ResultSortContext'

const THRESHOLD_RATIO = 0.75
const DWELL_MS = 1000
/** Max time to wait for the experiment arm to resolve before firing the
 * impression anyway. Bounds the worst case where PostHog is blocked and
 * `ready` never flips — better a rare untagged/null-arm impression than
 * permanently suppressing impressions (which would corrupt CTR far more). */
const READY_FALLBACK_MS = 4000

function clampThresholdRatio(raw: number | undefined): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    return THRESHOLD_RATIO
  }
  if (raw <= 0) return 0
  if (raw >= 1) return 1
  return raw
}

function clampDwellMs(raw: number | undefined): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 0) {
    return DWELL_MS
  }
  return raw
}

/** Payload shape emitted alongside every `Result Viewed` event. */
export interface ResultImpressionPayload {
  /** `results.hash` — joins to /api/dupes response and downstream link_clicks. */
  hash?: string | null
  /** Zero-based row index within the surface. */
  position?: number
  /** Canonical shop key (e.g. `https://www.wayfair.com`). */
  shop_key?: string | null
  /** Surface name (e.g. `ourpicks`, `dupe`, `sidepanel`, `restyle`, `chat`). */
  source?: string
  /** True when this card is one of the curated "Our Picks". */
  pick?: boolean
  /** Placement: sponsored `rail` vs organic `grid`. Added for
   * `sponsored-picks-rail-v2` so rail-vs-grid CTR and index-in-rail were
   * computable — the `pick` flag alone does not identify a rail once it is
   * backfilled with non-pick best matches. That rail was deleted on 2026-09-16,
   * so `'rail'` is no longer emitted; the chat grid stamps `'grid'`, the
   * marketplaces surfaces stamp `'marketplace'` (DUP-2048), the v3 inline
   * picks row stamps `'inline'`, and other surfaces leave it null. */
  section?: ResultSortContextValue['section']
  /** Which store's tab a `marketplace`-section card came from. Null elsewhere.
   *  Carries per-store CTR, which is the question the Marketplaces rail exists
   *  to answer. */
  marketplace?: string | null
  /** Which stores were OFFERED on this turn, comma-joined in pill order. Null
   *  outside the Marketplaces rail.
   *
   *  ⚠️ This is the DENOMINATOR for every per-store rate. The pill set is
   *  category-dependent (DUP-2048), so Wayfair is only shown on furniture-ish
   *  turns — its tap rate would read as excellent purely because it is never
   *  offered where it would lose. Without this on the event there is no honest
   *  way to reconstruct what each store was actually up against. */
  marketplaces_offered?: string | null
  /** Tier from `domainConfigsFeatured` — currently a numeric rank (matches
   * `VisualMatch.tier`). String tolerated if upstream ever switches form. */
  tier?: number | string | null
  /** Active sort criterion (`recommended`, `price`, `match`). */
  sort_criteria?: string | null
  /** Default-sort arm the user was on when this result rendered. Its only
   * producer, `rerank-default-sort`, concluded LOST and was removed on
   * 2026-09-16, so this is null on every impression today — the column is kept
   * because it holds historical per-arm rows and the next experiment on this
   * surface fills it. It was a PostHog super property too; that registration is
   * gone, and this explicit field is why Tinybird ever got the value. */
  default_sort_variant?: string | null
  /** Ranker version stamp. Defaults to `RANKER_VERSION` from common/constants
   * if the caller doesn't override — bump that constant when Phase 2 ships. */
  ranker_version?: string | null
  /** True when this card position was perturbed by an exploration arm.
   * Null until the randomization-budget feature lands. */
  is_exploration?: boolean | null
  /** Brain v2 chat thread ID — join key for chat surfaces where `hash` may
   * be absent (e.g. ProductRow feeds with `resultsData={}` as placeholder). */
  threadId?: string | null
  /** Text search query typed by the user. Null for URL/image searches. */
  query?: string | null
  /** Product category resolved server-side (e.g. `Fashion`, `Furniture`).
   * Null when the API could not classify. */
  query_category?: string | null
  /** Input modality (`url` | `image` | `text` | null) of the search that
   * produced this result. Makes per-modality CTR a native PostHog breakdown
   * instead of requiring a hash-join to Tinybird. Null on surfaces with no
   * modality (e.g. chat-message carousels). `text` is reserved — text
   * searches don't reach the dupes endpoints today. */
  input_type?: 'url' | 'image' | 'text' | null
  /** Origin of a v2 chat grid. Null outside chat product surfaces. */
  pathway?: ChatResultPathway | null
  /** Source-product hash for pasted URL turns only. */
  source_hash?: string | null
  /** Served catalog-search path for this result (`marqo` | `marqo_fallback` |
   * `v6` | `legacy` | null). The marqo arm is cache-isolated, so its rows never
   * reach the `visual_matches` table/Tinybird datasource — stamping the version
   * here is the ONLY impression-side denominator that can segment the
   * marqo↔PUMICE A/B by served path. The click event carries the same value via
   * `result.catalogSearchVersion`. Null on non-catalog (Google/Lens) results. */
  catalog_search_version?: string | null
  /** Item price — the raw `match.price.extracted_value` in the result's OWN
   * currency (a separate `match.price.currency` field, not normalized to USD
   * here; the click event stamps the same raw value, so numerator/denominator
   * stay consistent — but filter `currency='USD'` in any cross-item price-bucket
   * cut to avoid mixing currencies). Stamped on the impression so CTR can be
   * segmented by price bucket per arm — the marqo↔PUMICE A/B showed marqo
   * surfaces a pricier above-the-fold, and the click event already carries price
   * (`result.price.extracted_value`) but the impression (denominator) did not,
   * blocking a CTR-by-price-bucket read. Null when the result has no extracted
   * price (a genuine `0` is preserved, not coerced to null). */
  price?: number | null
  /** Currency of `price` (`match.price.currency`, e.g. `USD`). Stamped so the
   * `currency='USD'` filter the `price` doc calls for is actually expressible on
   * the impression (denominator) — without it, price buckets would silently mix
   * currencies. Null when the result has no price. */
  currency?: string | null
  /** Interleaving batch id (DUP-1022 / DUP-1776) — the impression-side
   * cross-check and viewability denominator for the win-rate readout. The click
   * event carries the same `ibid`, so clicks-per-grid can be computed
   * dwell-gated. Null off the harness. */
  ibid?: string | null
  /** Arm (`a` | `b`) that drafted this tile — enables per-arm impression /
   * viewability counts. Null off the harness. */
  ilarm?: 'a' | 'b' | null
  /** What this impression STANDS FOR. `'product'` is one product card, the only
   * value before stacked cards existed and the default everywhere. `'stack'` is
   * a single cell standing for `stacked_count` products from one shop, none of
   * which fire their own impression until the shopper opens the drawer.
   * `'more'` is the Wayfair row's "See all NN results" card: a stack-like cell for the
   * family rows the row did not show.
   *
   * ⚠️ **This field changes what a denominator means, so every readout that
   * counts `Result Viewed` rows has to choose.** A stacked grid emits ONE row
   * where it used to emit `n`. Filter `cell_type = 'product'` to keep the old
   * per-product meaning; count all rows to measure cells-on-screen. Silence is
   * the one option that isn't available — see `docs/features/active/marketplaces/decisions.md`. */
  cell_type?: 'product' | 'stack' | 'more' | null
  /** How many products the cell stands for. `null` on a `'product'` cell (not
   * `1` — null and one are different claims, and a `1` here would quietly pass
   * a `sum(stacked_count)` reconstruction of the old denominator).
   *
   * This is `stack.count` — every product the cell REPLACED, face card
   * included — because the question it answers is "how many `Result Viewed`
   * rows would this cell have produced before?". ⚠️ Not `count - 1` (the
   * rows tucked behind): read that and every reconstruction is off by one per stack.
   * (The count badge shows `count` too since the deck redesign; the old `+N`
   * chip showed `count - 1`, so screenshots from before it differ.)
   *
   * ⚠️ At stage 1 it is bounded by our own fetch shape, not by the store's
   * depth — the v6 path caps a shop at `MAX_PER_BRAND_DEFAULT * VARIANT_HEADROOM`
   * rows before hydration. Do not read it as store inventory. */
  stacked_count?: number | null
  /** Which CONTAINER the card rendered in. Strictly finer than `section`: a
   * marketplace-tab row and a stack-drawer row are both `section:
   * 'marketplace'`, but the drawer row cost a tap to reach.
   *
   * ⚠️ **Load-bearing for the click↔impression join.** `ctr_by_cell` joins on
   * `(session_id, hash, result_index)`; a stack drawer re-uses the TURN's hash
   * with a stack-local index, so a drawer row at index 2 collides with the
   * organic grid's position 2. Those cells feed `ranking_scores`, so the
   * mis-join reaches production ranking. Keep this on both the impression and
   * the click, and keep it in the join key. */
  impression_source?:
    | 'grid'
    | 'rail'
    | 'marketplace_tab'
    | 'stack_drawer'
    | null
  /** Text-search grid id (`text-search-<32 hex>`), joining this impression to
   * the retrieval leg that supplied the tile. The click event carries the same
   * value, so per-source CTR has a numerator and a denominator that agree.
   * Without it here, only the server-side Tinybird -> link_clicks join works
   * and the PostHog-side rate is unusable. Null outside the text path. */
  text_grid_hash?: string | null
  /** Retrieval leg that served this tile (`catalog`, `google_lens`,
   *  `google_shopping`, `bing`, ...), stamped server-side from `source_api`.
   *  Lets CTR be read per source instead of for one mixed grid. Null when the
   *  row lost its source stamp or the surface predates tagging. */
  leg?: ResultLeg | null
  /** 0-based index in the list the server sent in this response. */
  served_index?: number | null
  /** 0-based slot in a relevance-only ordering of the same grid (no Our
   *  Picks float, pin, AMP splice or other boost); null for a tile that was
   *  never a ranked candidate. Server-stamped as `VisualMatch.naturalIndex`. */
  natural_index?: number | null
  /** 0-based index as rendered, after any client reorder. Same value as
   *  `position`; named explicitly so readouts never confuse it with
   *  `served_index`. */
  displayed_index?: number | null
  /** PUMICE cosine vs the source image (0-1). Image searches only: the text
   *  path does not compute it, so it is null there. */
  visual_score?: number | null
  /** Stack drawer section the card rendered in: `'top'` (the stack's own
   *  rows) or `'more_from'` (the "More from <store>" rows fetched on open).
   *  Null outside a drawer. Both sections share the turn hash and one
   *  continuous `position`, so this is the only way to tell them apart. The
   *  click event and `link_clicks.metadata` carry the same value. */
  drawer_section?: 'top' | 'more_from' | null
  /** 0-based slot within `drawer_section`, in list order (a masonry layout
   *  moves cards between columns but not in this order). Unlike `position`,
   *  it restarts at 0 for `'more_from'`. Null outside a drawer. */
  section_slot?: number | null
  /** Rows in the opened stack (`stack.count`). Null outside a drawer.
   *  ⚠️ Deliberately NOT `stacked_count`: readouts use `stacked_count` with
   *  `cell_type` to find stack CELLS, and leaf rows must keep that null. */
  stack_size?: number | null
  /** Grid position of the stack cell this drawer was opened from — the
   *  `position` on its own impression and on `Retailer Stack Opened`. */
  stack_position?: number | null
  /** One id per drawer open, also on `Retailer Stack Opened`. Joins an open to
   *  its impressions, clicks and, via `link_clicks.metadata`, its sales. */
  drawer_open_id?: string | null
}

/** Optional per-call overrides. */
export interface UseResultImpressionOptions {
  /** When false, hook is a no-op (e.g. placeholder, admin, flagged, streaming). */
  enabled?: boolean
  /** Soft gate (default true): when false, DEFER firing `Result Viewed` until
   * the surface's sort has settled, so the impression is never tagged with a
   * pre-resolution sort order (which would double-count / mis-tag a CTR
   * baseline — the original case was `rerank-default-sort` flipping the
   * default lens on a cold load, and the legacy `/[hash]` page still feeds
   * this the readiness of the live v1 `sponsored-picks-rail` flag). Unlike
   * `enabled` (a hard no-op), this only delays — after `READY_FALLBACK_MS` the
   * impression fires regardless, so a blocked PostHog can't permanently
   * suppress impressions. */
  ready?: boolean
  /** Override the IntersectionObserver visibility ratio (0..1). Default 0.75. */
  thresholdRatio?: number
  /** Override the continuous-visibility dwell duration (ms). Default 1000. */
  dwellMs?: number
}

/**
 * Fires `Result Viewed` once when the referenced element has been continuously
 * visible above `thresholdRatio` for `dwellMs`. Cleans up timer + observer on
 * unmount or if the element scrolls out before the delay elapses.
 *
 * - `dwell_ms`, `ranker_version`, and `resultIndex` (alias of `position`) are
 *   auto-attached to the payload.
 * - Emits to Tinybird only (`analytics_events`). PostHog / GA / Plausible /
 *   Facebook are intentionally skipped — impression volume is ~50× click
 *   volume (it was the single largest PostHog event at ~32% of all ingestion).
 *   Tinybird is the durable home: `result_viewed_events` → `ctr_by_cell` (the
 *   Phase 2 ranking input) read straight from `analytics_events`, independent
 *   of any PostHog sink. PostHog only ever consumed this as the denominator of
 *   the `rerank-default-sort` experiment (374206); its owner accepted losing
 *   that PostHog metric (2026-06-08), and the experiment itself was retired on
 *   2026-09-16, so nothing is waiting on it. See
 *   `docs/references/posthog-cost-reduction.md` Task 4.
 * - To tune the global defaults, change THRESHOLD_RATIO / DWELL_MS at the top
 *   of this file and redeploy. Callers can use options for local overrides.
 * - The ref must already point at an element when this effect runs; conditional
 *   callers should hold `enabled=false` until their card is mounted.
 */
export function useResultImpression(
  ref: React.RefObject<Element | null>,
  payload: ResultImpressionPayload,
  options?: UseResultImpressionOptions
) {
  const { track } = useAnalytics()

  const impressionFired = useRef(false)
  const payloadRef = useRef(payload)
  payloadRef.current = payload

  const enabled = options?.enabled !== false
  const ready = options?.ready !== false
  const thresholdRatio = clampThresholdRatio(options?.thresholdRatio)
  const dwellMs = clampDwellMs(options?.dwellMs)

  // Safety fallback: if the arm hasn't resolved within READY_FALLBACK_MS, treat
  // the impression as ready anyway so a stalled/blocked PostHog can't suppress
  // it forever. Only armed while genuinely waiting (enabled but not ready).
  const [readyTimedOut, setReadyTimedOut] = useState(false)
  useEffect(() => {
    if (ready || readyTimedOut || !enabled) return
    const t = setTimeout(() => setReadyTimedOut(true), READY_FALLBACK_MS)
    return () => clearTimeout(t)
  }, [ready, readyTimedOut, enabled])
  const effectiveReady = ready || readyTimedOut

  useEffect(() => {
    if (!enabled || !effectiveReady || impressionFired.current) return
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') return

    let timer: ReturnType<typeof setTimeout> | null = null
    let visibleSince: number | null = null

    const clearPending = () => {
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      visibleSince = null
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Guard the partial-crossing race: IO fires `isIntersecting=true` when
        // bounds overlap the root, which can happen while the ratio is still
        // below the configured threshold during a downward scroll.
        const meetsThreshold =
          !!entry?.isIntersecting && entry.intersectionRatio >= thresholdRatio

        if (meetsThreshold && !impressionFired.current && !timer) {
          visibleSince = performance.now()
          timer = setTimeout(() => {
            if (impressionFired.current) return
            impressionFired.current = true
            const dwell =
              visibleSince !== null
                ? Math.round(performance.now() - visibleSince)
                : dwellMs
            const p = payloadRef.current
            track(
              AnalyticsEventName.ResultViewed,
              {
                ...p,
                resultIndex: p.position,
                ranker_version: p.ranker_version ?? RANKER_VERSION,
                is_exploration: p.is_exploration ?? null,
                dwell_ms: dwell,
              },
              { skipGA: true, skipPosthog: true, sendToTinybird: true }
            )
            observer.disconnect()
          }, dwellMs)
        } else if (!meetsThreshold) {
          clearPending()
        }
      },
      { threshold: thresholdRatio }
    )
    observer.observe(el)
    return () => {
      clearPending()
      observer.disconnect()
    }
    // track is stable (useCallback in useAnalytics) but included so identity
    // changes after auth/tempUser hydration pick up the correct closure.
  }, [enabled, effectiveReady, thresholdRatio, dwellMs, track])
}

export default useResultImpression
